<template>
  <div class="divide-y-2">
    <!-- Категории -->
    <div v-if="!hideFilters.includes('currentCategory')">
      <p class="font-semibold text-xl mb-4">{{ $t('page.categories') }}</p>
      <div class="h-96 overflow-y-scroll fltrs">
        <ul class="space-y-3 row-list">
          <li>
            <router-link
              :class="{ '!text-accent-color': currentCategory == null }"
              class="text-main-color text-base font-semibold hover:text-accent-color"
              :to="{ name: routeName, query: getQuery({ category: null }) }"
            >
              {{ $t('page.all') }}
            </router-link>
          </li>

          <li v-for="cat in $store.publicationCategories" :key="cat.id">
            <router-link
              :class="{ '!text-accent-color': cat.id == currentCategory }"
              class="text-main-color text-base font-semibold hover:text-accent-color"
              :to="{ name: routeName, query: getQuery({ category: cat.id }) }"
            >
              {{ cat.name }}
              <span class="text-red-400 text-sm">({{ cat.objects__count }})</span>
            </router-link>
          </li>
        </ul>
      </div>
    </div>

    <!-- Время публикации -->
    <div v-if="!hideFilters.includes('publishedDate')" class="my-5 py-5">
      <p class="font-semibold text-xl mb-4">{{ $t('page.publishedDate') }}</p>
      <div class="overflow-y-scroll fltrs">
        <select class="p-2 w-full" v-model="selectedPublished" @change="onDateChanged">
          <option value="">{{ $t('page.allTime') }}</option>
          <option value="1h">{{ $t('page.lastOneHour') }}</option>
          <option value="5h">{{ $t('page.lastFiveHour') }}</option>
          <option value="1d">{{ $t('page.lastOneDay') }}</option>
          <option value="3d">{{ $t('page.lastThreeDay') }}</option>
          <option value="7d">{{ $t('page.lastSevenDay') }}</option>
          <option value="14d">{{ $t('page.lastFourteenDay') }}</option>
          <option value="1m">{{ $t('page.lastOneMonth') }}</option>
        </select>
      </div>
    </div>

    <!-- Подписка и VIP -->
    <div v-if="!hideFilters.includes('currentSubscription')" class="my-5 py-5">
      <p class="font-semibold text-xl mb-4">{{ $t('page.status') }}</p>
      <div class="overflow-y-scroll fltrs">
        <ul class="space-y-3 row-list">
          <li>
            <router-link
              :class="{ '!text-accent-color': currentSubscription == null }"
              class="text-main-color text-base font-semibold hover:text-accent-color"
              :to="{ name: routeName, query: getQuery({ subscription: null }) }"
            >
              {{ $t('page.all') }}
            </router-link>
          </li>

          <li v-for="(name, val) in subscriptionLevels" :key="val">
            <router-link
              :class="{ '!text-accent-color': currentSubscription == val }"
              class="text-main-color text-base font-semibold hover:text-accent-color"
              :to="{ name: routeName, query: getQuery({ subscription: val }) }"
            >
              {{ name }}
            </router-link>
          </li>

          <li v-if="!hideFilters.includes('isVip')">
            <router-link
              :class="{ '!text-accent-color': isVip === 'true' }"
              class="text-main-color text-base font-semibold hover:text-accent-color"
              :to="{
                name: routeName,
                query: getQuery({ is_vip: isVip === 'true' ? null : true }),
              }"
            >
              VIP
            </router-link>
          </li>
        </ul>
      </div>
    </div>

    <!-- Цена -->
    <div v-if="!hideFilters.includes('currentPrice')" class="my-5 py-5">
      <p class="font-semibold">{{ $t('page.price') }}</p>
      <div class="mt-5">
        <div class="flex gap-7 mt-5">
          <div class="flex flex-col">
            <label for="minPrice">{{ $t('page.min') }}</label>
            <input
              v-model.number="minPriceQuery"
              @input="debouncedOnChange"
              type="number"
              min="0"
              id="minPrice"
              class="w-full font-medium text-sm py-1 px-3 rounded border border-gp-grey-90"
            />
          </div>

          <div class="flex flex-col">
            <label for="maxPrice">{{ $t('page.max') }}</label>
            <input
              v-model.number="maxPriceQuery"
              @input="debouncedOnChange"
              type="number"
              id="maxPrice"
              class="w-full font-medium text-sm py-1 px-3 rounded border border-gp-grey-90"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Динамические фильтры -->
    <div>
      <slot name="dynamic"></slot>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash.debounce';
import { useFilterStore } from '@/store/filterStore.js';

export default {
  name: 'PublicationSide',
  props: {
    routeName: { type: String, default: 'stores' },
    hideFilters: { type: Array, default: () => [] },

    currentCategory: { type: [Number, String], default: undefined },
    currentSubscription: { type: [Number, String], default: undefined },
    isVip: { type: [Boolean, String], default: undefined },

    // ⚠️ Оставляем prop, чтобы ничего не ломать у родителей,
    // но PublicationSide больше НЕ пушит price__range в URL.
    currentPrice: { type: [String], default: undefined },

    currentSort: { type: [Number, String], default: undefined },
    currentPublished: { type: [String], default: undefined },
  },

  data() {
    return {
      filterStore: useFilterStore(),

      // UI
      minPriceQuery: null,
      maxPriceQuery: null,
      selectedPublished: this.currentPublished ?? '',

      subscriptionLevels: {
        1: 'GOLD',
        2: 'SILVER',
        3: 'BRONZE',
      },
    };
  },

  watch: {
    // ✅ синхронизируем select "published"
    currentPublished: {
      handler(newVal) {
        this.selectedPublished = newVal ?? '';
      },
      immediate: true,
    },

    // ✅ если родитель всё-таки передаст currentPrice (старое поведение) —
    // просто подхватим в инпуты, но НИКУДА не пушим.
    currentPrice: {
      handler(newVal) {
        if (newVal) {
          const [min, max] = String(newVal).split('__');
          this.minPriceQuery = min !== undefined && min !== '' ? Number(min) : null;
          this.maxPriceQuery = max !== undefined && max !== '' ? Number(max) : null;
        } else {
          this.minPriceQuery = null;
          this.maxPriceQuery = null;
        }
      },
      immediate: true,
    },
  },

  computed: {
    debouncedOnChange() {
      return _debounce(this.pushPriceFilter, 500);
    },
  },

  methods: {
    // ✅ только query для router-link (категории/подписки/vip) — как было
    getQuery(overrides = {}) {
      return {
        category: this.currentCategory,
        subscription: this.currentSubscription,
        is_vip: this.isVip,
        sort_by: this.currentSort, // было ordering — но у тебя в publications.vue читается sort_by
        published: this.currentPublished,
        ...overrides,
      };
    },

    // ✅ Цена: обновляем ТОЛЬКО filterStore + dynamicCount
    // Никаких router.push -> значит фильтрация публикаций останется "по кнопке"
    pushPriceFilter() {
      const min = this.minPriceQuery ?? 0;
      const max = this.maxPriceQuery ?? null;

      this.filterStore.updateFilter({
        type: 'range',
        property: 999, // price property id
        value: [min, max],
      });

      // только пересчёт счетчика
      this.filterStore.fetchDynamicCount({ mode: 'include' });
    },

    // ✅ Published: только filterStore + dynamicCount (публикации по кнопке)
    onDateChanged(e) {
      const date = e.target.value || '';
      this.selectedPublished = date;

      this.filterStore.updateFilter({ type: 'published', value: date });
      this.filterStore.fetchDynamicCount({ mode: 'include' });
    },
  },
};
</script>
